import { type Time } from '@discover-data/stores/restaurant/model/times/model';
import formatTime from '@discover-data/stores/restaurant/model/times/format';
import useEndTime from '@discover-data/stores/hooks/times/end/useEndTime';
import useResetTimes from '@discover-data/stores/hooks/times/useResetTimes';
import useStartTime from '@discover-data/stores/hooks/times/start/useStartTime';
import DynamicDialog, { type DynamicDialogProps } from '@discover-ui/dynamicDialog';
import Button from '@discover-ui/button';
import TimeRangeFilter from './range';

function label(startTime: Time, endTime: Time) {
  // both start and end
  if (startTime !== null && endTime !== null) {
    if (startTime === endTime) {
      return `At ${formatTime(startTime)}`;
    }

    return `${formatTime(startTime)} to ${formatTime(endTime)}`;
  }

  // start only
  if (startTime) {
    return `From ${formatTime(startTime)}`;
  }

  // end only
  if (endTime) {
    return `Until ${formatTime(endTime)}`;
  }

  // no times
  return 'Time';
}

export type TimeRangeProps = Omit<
  DynamicDialogProps,
  | 'triggerLabel'
  | 'dialogTitle'
  | 'dialogTitleVisible'
  | 'dialogDescription'
  | 'dialogDescriptionVisible'
  | 'contentClassName'
  | 'children'
>;

export default function TimeRangeDialog({ triggerProps, ...props }: TimeRangeProps) {
  const [startTime] = useStartTime();
  const [endTime] = useEndTime();
  const resetTimes = useResetTimes();

  const hasStartTime = startTime !== null;
  const hasEndTime = endTime !== null;
  const hasSomeTime = hasStartTime || hasEndTime;

  return (
    <DynamicDialog
      triggerLabel={label(startTime, endTime)}
      dialogTitle="Select Time"
      dialogTitleVisible
      dialogDescription={
        <span className="flex flex-col items-start gap-2 p-1 text-start text-black">
          <strong>Set the Scene: Choose Your Perfect Dining Window</strong>
          <span>
            Select your ideal start and end times to discover the best tables available when you
            want them.
          </span>
        </span>
      }
      dialogDescriptionVisible
      contentClassName="flex flex-col p-1"
      triggerProps={triggerProps}
      {...props}>
      {({ setOpen }) => (
        <div className="flex flex-1 flex-col items-stretch gap-3 p-1">
          <TimeRangeFilter className="my-2" />
          <div className="mt-auto flex flex-row justify-between gap-1">
            <Button
              width="wide"
              kind={hasSomeTime ? 'outline' : 'gray'}
              disabled={!hasSomeTime}
              scale="md"
              onClick={() => {
                resetTimes();
              }}>
              Clear
            </Button>
            <Button width="wide" onClick={() => setOpen(false)}>
              Done
            </Button>
          </div>
        </div>
      )}
    </DynamicDialog>
  );
}
